import {inject, Injectable} from '@angular/core';
import {StorageService} from './storage.service';
import {Router} from '@angular/router';
import {NzMessageService} from 'ng-zorro-antd/message';
import {environment} from '../../environments/environment';
import {MqttService} from './mqtt.service';
import {uuidv7} from 'uuidv7';
import {RegionService} from './region.service';
import {SupabaseAuthService} from './supabase-auth.service';
import {RegionCode} from '../app.env.config';

@Injectable({
  providedIn: 'root',
})
export class AppInitService {
  private readonly storageService = inject(StorageService);
  private readonly router = inject(Router);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly mqttService = inject(MqttService);
  private readonly regionService = inject(RegionService);
  private readonly supabaseAuthService = inject(SupabaseAuthService);

  async init() {
    // 确保 clientId 存在（install_id，首次生成后不变）
    const clientId = await this.storageService.lfGet('clientId');
    if (!clientId) {
      await this.storageService.lfSet('clientId', uuidv7());
    }

    // 第三方统计不挡首屏：idle 后再动态加载 Firebase / Clarity。
    this.scheduleTelemetry();

    // Development builds default to LOCAL (debug-only regions require debugMode).
    let regionCode = await this.storageService.lfGet('region') as string | null;
    if (!regionCode) {
      if (environment.production) {
        regionCode = RegionCode.US;
      } else {
        await this.storageService.lfSet('debugMode', true);
        regionCode = RegionCode.LOCAL;
      }
    } else if (
      !environment.production
      && (
        regionCode === RegionCode.LOCAL
        || regionCode === RegionCode.DEV
        || regionCode === RegionCode.LOCAL_PROD
      )
    ) {
      await this.storageService.lfSet('debugMode', true);
    }
    await this.regionService.setRegion(regionCode);

    // OAuth returns with a PKCE authorization code but no session yet. Let the
    // callback route exchange the code before applying version/session guards.
    if (this.isPublicAuthPath() || this.isDebugSettingRoute()) {
      return;
    }

    const version = await this.storageService.lfGet('version');
    if (!version || version !== environment.storageVersion) {
      this.nzMessageService.info(
        version ? 'New data version required, please Login.' : 'Please Login...',
        {nzDuration: 3000},
      );
      await this.supabaseAuthService.signOutCurrentSession().catch(() => undefined);
      await this.router.navigate(['/account/login']);
      return;
    }

    const session = await this.supabaseAuthService.getSession();
    if (!session?.access_token) {
      this.nzMessageService.info('Please Login...', {nzDuration: 3000});
      await this.router.navigate(['/account/login']);
      return;
    }

    await this.mqttService.init();
  }

  private scheduleTelemetry(): void {
    const run = () => {
      void this.initTelemetry();
    };
    if (typeof globalThis.requestIdleCallback === 'function') {
      globalThis.requestIdleCallback(run, {timeout: 4000});
      return;
    }
    globalThis.setTimeout(run, 3000);
  }

  private async initTelemetry(): Promise<void> {
    if (environment.production) {
      try {
        const {default: Clarity} = await import('@microsoft/clarity');
        Clarity.init('t1rlita7fo');
      } catch (e) {
        console.log('clarity init error', e);
      }
    }

    try {
      if (environment.production && environment.firebaseConfig?.apiKey) {
        const [{initializeApp}, {getAnalytics, setUserProperties}, {getPerformance}] = await Promise.all([
          import('firebase/app'),
          import('firebase/analytics'),
          import('firebase/performance'),
        ]);
        const app = initializeApp(environment.firebaseConfig);
        const analytics = getAnalytics(app);
        setUserProperties(analytics, {app_version: environment.version});
        getPerformance(app);
      }
    } catch (e) {
      console.log('firebase init error', e);
    }
  }

  private isPublicAuthPath(): boolean {
    const path = globalThis.location?.pathname ?? '';
    return (
      path === '/auth/oauth-callback'
      || path.startsWith('/auth/oauth-callback/')
      || path === '/account/login'
      || path === '/account/register'
      || path === '/account/reset-password'
    );
  }

  private isDebugSettingRoute(): boolean {
    return globalThis.location?.pathname.replace(/\/+$/, '') === '/debug-setting';
  }
}
